import { cn } from '@/lib/cn'

/*
 * 点阵数字。字形取自 alfaplus-hp 的 px-0…px-9（alfaPlus lab，CC BY 4.0），
 * 和那边的像素 α、件号 A-07 是同一套：12 行网格、笔画 2 格、字身占 r1–r10。
 * 这里只画字身那 10 行，所以 1× 就是 10px 高；每个字 7 格宽，字距 1 格。
 *
 * 只认 0–9 和「+」（未读数截断时的 99+）。别的字符原样丢掉 ——
 * 调用方本来就只传计数；屏幕阅读器读的是外层的 aria-label，不读这张图。
 */
type Row = readonly [x: number, y: number, w: number]

// prettier-ignore
const GLYPHS: Record<string, readonly Row[]> = {
  '0': [[1, 1, 5], [0, 2, 2], [5, 2, 2], [0, 3, 2], [5, 3, 2], [0, 4, 2], [5, 4, 2], [0, 5, 2], [5, 5, 2], [0, 6, 2], [5, 6, 2], [0, 7, 2], [5, 7, 2], [0, 8, 2], [5, 8, 2], [0, 9, 2], [5, 9, 2], [1, 10, 5]],
  '1': [[3, 1, 2], [1, 2, 4], [3, 3, 2], [3, 4, 2], [3, 5, 2], [3, 6, 2], [3, 7, 2], [3, 8, 2], [3, 9, 2], [1, 10, 6]],
  '2': [[1, 1, 5], [0, 2, 2], [5, 2, 2], [5, 3, 2], [5, 4, 2], [4, 5, 2], [3, 6, 2], [2, 7, 2], [1, 8, 2], [0, 9, 2], [0, 10, 7]],
  '3': [[1, 1, 5], [0, 2, 2], [5, 2, 2], [5, 3, 2], [5, 4, 2], [2, 5, 4], [5, 6, 2], [5, 7, 2], [5, 8, 2], [0, 9, 2], [5, 9, 2], [1, 10, 5]],
  '4': [[5, 1, 2], [4, 2, 3], [3, 3, 4], [2, 4, 2], [5, 4, 2], [1, 5, 2], [5, 5, 2], [0, 6, 2], [5, 6, 2], [0, 7, 7], [0, 8, 7], [5, 9, 2], [5, 10, 2]],
  '5': [[0, 1, 7], [0, 2, 2], [0, 3, 2], [0, 4, 2], [0, 5, 6], [5, 6, 2], [5, 7, 2], [5, 8, 2], [0, 9, 2], [5, 9, 2], [1, 10, 5]],
  '6': [[2, 1, 4], [1, 2, 2], [0, 3, 2], [0, 4, 2], [0, 5, 6], [0, 6, 2], [5, 6, 2], [0, 7, 2], [5, 7, 2], [0, 8, 2], [5, 8, 2], [0, 9, 2], [5, 9, 2], [1, 10, 5]],
  '7': [[0, 1, 7], [5, 2, 2], [4, 3, 2], [4, 4, 2], [3, 5, 2], [3, 6, 2], [2, 7, 2], [2, 8, 2], [1, 9, 2], [1, 10, 2]],
  '8': [[1, 1, 5], [0, 2, 2], [5, 2, 2], [0, 3, 2], [5, 3, 2], [0, 4, 2], [5, 4, 2], [1, 5, 5], [0, 6, 2], [5, 6, 2], [0, 7, 2], [5, 7, 2], [0, 8, 2], [5, 8, 2], [0, 9, 2], [5, 9, 2], [1, 10, 5]],
  '9': [[1, 1, 5], [0, 2, 2], [5, 2, 2], [0, 3, 2], [5, 3, 2], [0, 4, 2], [5, 4, 2], [2, 5, 5], [5, 6, 2], [5, 7, 2], [5, 8, 2], [4, 9, 2], [1, 10, 4]],
  // 「+」在这套字形里没有，照同样的笔画补一个，比数字窄一格
  '+': [[2, 3, 2], [2, 4, 2], [0, 5, 6], [0, 6, 6], [2, 7, 2], [2, 8, 2]],
}

const ADVANCE: Record<string, number> = { '+': 7 }
const DEFAULT_ADVANCE = 8

export function PixelDigits({
  value,
  scale = 1,
  className,
}: {
  value: string | number
  /** 整数倍放大，别传小数：半格会糊边 */
  scale?: number
  className?: string
}) {
  const chars = [...String(value)].filter((char) => char in GLYPHS)
  let cursor = 0
  const placed = chars.map((char) => {
    const x = cursor
    cursor += ADVANCE[char] ?? DEFAULT_ADVANCE
    return { char, x }
  })
  // 最后一个字不需要后面那 1 格字距
  const width = Math.max(cursor - 1, 1)

  return (
    <svg
      viewBox={`0 1 ${width} 10`}
      width={width * scale}
      height={10 * scale}
      shapeRendering="crispEdges"
      fill="currentColor"
      aria-hidden
      className={cn('inline-block shrink-0', className)}
    >
      {placed.map(({ char, x }, index) =>
        GLYPHS[char].map(([rx, ry, rw]) => (
          <rect key={`${index}-${rx}-${ry}`} x={x + rx} y={ry} width={rw} height={1} />
        )),
      )}
    </svg>
  )
}
